import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

// Components
import { Button } from "@/components/Primitives/Button"
import { Text } from "@/components/Primitives/Text"
import { Card } from "@/components/Primitives"
import { AuthLayout } from "@/components/Primitives/AuthLayout"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { Toaster } from "@/components/Primitives/Sonner/sonner"

// Hooks
import { useConfig } from "@/hooks/useConfig"
import { useSignupStore } from "@/lib/store/signupStore"
import { useToast } from "@/hooks/useToast"
import { signupLink } from "@/lib/api/auth"

const RESEND_COOLDOWN_SECONDS = 30

export function SignupEmailPage() {
  const { t, tp } = useConfig()
  const { email } = useSignupStore()
  const { success, error } = useToast()
  const navigate = useNavigate()

  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS)
  const [isResending, setIsResending] = useState(false)

  useEffect(() => {
    if (resendTimer <= 0) return

    const interval = setInterval(() => {
      setResendTimer((prev) => Math.max(0, prev - 1))
    }, 1000)

    return () => clearInterval(interval)
  }, [resendTimer])

  const handleResendLink = async () => {
    try {
      setIsResending(true)
      await signupLink(email)
      success(t("config_sign_up_success_toast"))
      setResendTimer(RESEND_COOLDOWN_SECONDS)
    } catch (err) {
      console.error("Resend failed:", err)
      error(t("config_sign_up_error_generic"))
    } finally {
      setIsResending(false)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const getButtonText = () => {
    if (isResending) return t("config_sign_up_button_submitting")
    if (resendTimer > 0)
      return `${t("config_sign_up_success_button")} (${formatTime(
        resendTimer,
      )})`
    return t("config_sign_up_success_button")
  }

  return (
    <>
      <Toaster />
      <AuthLayout
        description={t("config_sign_up_hero_description")}
        imageUrl="/images/mailbox.webp"
      >
        <Card className="flex flex-col p-8">
          <div>
            <Text variant="h3" bold>
              {t("config_sign_up_success_title")}
            </Text>

            <MarkdownRenderer
              variant="paragraph"
              content={tp("config_sign_up_success_body", { email })}
              className="text-neutral-700"
            />

            <Button
              variant="tertiary"
              className="font-semibold w-full mt-6 md:mt-4"
              onClick={() => navigate(-1)}
            >
              Change Email
            </Button>
          </div>

          <div className="border-t border-gray-300 w-full" />

          <Text variant="small" className="text-gray-700">
            {t("config_sign_up_email_not_received")}
          </Text>

          <Button
            variant="tertiary"
            className="font-semibold w-full"
            onClick={handleResendLink}
            disabled={resendTimer > 0 || isResending}
          >
            {getButtonText()}
          </Button>
        </Card>
      </AuthLayout>
    </>
  )
}
